import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    // 優先讀取 localStorage，否則使用系統偏好
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return [isDark, setIsDark] as const;
}

export function Header() {
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [isDark, setIsDark] = useDarkMode();
  const { showCoverImages, toggleCoverImages } = useAppSettings();

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <header className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl font-semibold text-primary-500 hover:text-primary-600 transition-colors"
          >
            小說收藏器
          </Link>

          <div className="flex items-center gap-1">
            {/* Navigation */}
            <nav className="flex items-center">
              <NavLink to="/" label="首頁" isActive={isActive('/', true)} />
              <NavLink to="/novels" label="書架" isActive={isActive('/novels')} />
              <NavLink to="/series" label="系列" isActive={isActive('/series')} />

              {/* Admin Dropdown */}
              {isAdmin && (
                <div className="relative group">
                  <button
                    className={`relative px-3 py-2 text-sm font-medium transition-colors ${isActive('/admin')
                      ? 'text-neutral-900 dark:text-neutral-50'
                      : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-50'
                      }`}
                  >
                    管理
                    <svg
                      className="inline-block w-4 h-4 ml-0.5 -mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {isActive('/admin') && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary-500 rounded-full" />
                    )}
                  </button>
                  <div className="absolute left-0 mt-1 w-44 card py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                    {/* 用戶管理 */}
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      用戶管理
                    </Link>

                    {/* 小說管理群組 */}
                    <div className="border-t border-neutral-100 dark:border-neutral-700 my-1" />
                    <div className="px-4 py-1 text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                      小說
                    </div>
                    <Link
                      to="/admin/upload"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      上傳小說
                    </Link>
                    <Link
                      to="/admin/add-novel"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      爬取小說
                    </Link>
                    <Link
                      to="/admin/search"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      搜尋小說
                    </Link>

                    {/* LLM 設定群組 */}
                    <div className="border-t border-neutral-100 dark:border-neutral-700 my-1" />
                    <div className="px-4 py-1 text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                      LLM
                    </div>
                    <Link
                      to="/admin/ai-console"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      AI 控制台
                    </Link>
                    <Link
                      to="/admin/analysis-tools"
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      分析 Tool 設定
                    </Link>
                  </div>
                </div>
              )}
            </nav>

            {/* Cover Image Toggle */}
            <button
              onClick={toggleCoverImages}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500 dark:text-neutral-400"
              title={showCoverImages ? '隱藏封面圖片' : '顯示封面圖片'}
            >
              {showCoverImages ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              )}
            </button>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500 dark:text-neutral-400"
              title={isDark ? '切換淺色模式' : '切換深色模式'}
            >
              {isDark ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </button>

            {/* User Menu */}
            <div className="relative ml-1">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="w-8 h-8 rounded-full ring-2 ring-neutral-100 dark:ring-neutral-700"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-medium">
                    {user?.name?.[0] || 'U'}
                  </div>
                )}
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-2 w-48 card py-1 z-20">
                    <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-700">
                      <div className="font-medium text-neutral-900 dark:text-neutral-50 truncate">
                        {user?.name}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                        {user?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      登出
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// Editorial 風格導航連結
function NavLink({ to, label, isActive }: { to: string; label: string; isActive: boolean }) {
  return (
    <Link
      to={to}
      className={`relative px-3 py-2 text-sm font-medium transition-colors ${isActive
        ? 'text-neutral-900 dark:text-neutral-50'
        : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-50'
        }`}
    >
      {label}
      {/* 底部活動指示線 */}
      {isActive && (
        <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary-500 rounded-full" />
      )}
    </Link>
  );
}
